// ==========================================================================
// FILE: /app/components/onboarding/steps/SettingsStep.tsx
// ==========================================================================
import { useState, type FC } from "react";
import { Switch } from "@progress/kendo-react-inputs";
import { ICON_BASE_PATH } from "@/app/utils/config";
import GxSmallDropdown from "@/app/components/common/actions/dropdowns/GxSmallDropdown";
import Mobilefiled from "@/app/components/common/actions/inputs-b/Mobilefiled";
import OldPasswordFiled from "@/app/components/common/actions/inputs-b/OldPasswordFiled";
import PasswordFiled from "@/app/components/common/actions/inputs-b/PasswordFiled";
import ConfirmPasswordfiled from "@/app/components/common/actions/inputs-b/ConfirmPasswordfiled";
import SecuritySelectDropdown from "@/app/components/common/actions/dropdowns/securitySelectDropdown";
import TextAreaInput from "@/app/components/common/actions/inputs-b/TextAreaInput";
import { GxLineStepper, type GxLineStepperStep } from "@/app/components/common/actions/Stepper/GxLineStepper";
import type { SettingsFormData } from "./types";

interface SettingsStepProps {
  data: SettingsFormData;
  updateField: (field: keyof SettingsFormData, value: any) => void;
  subStep: 0 | 1 | 2 | 3;
  setSubStep: (step: 0 | 1 | 2 | 3) => void;
}

const SETTINGS_SUBSTEPS: GxLineStepperStep[] = [
  { id: "preferences", label: "Preferences" },
  { id: "notifications", label: "Notifications" },
  { id: "security", label: "Security" },
  { id: "questions", label: "Security Questions" },
];

// dummy — static notification toggles standing in for the real
// notification-preferences API until this demo is wired to data.
const DUMMY_NOTIFICATIONS = [
  { id: "email", icon: "notifications.svg", label: "Email Notifications", defaultChecked: true },
  { id: "sms", icon: "notifications.svg", label: "SMS Notifications", defaultChecked: true },
  { id: "push", icon: "notifications.svg", label: "Push Notifications", defaultChecked: false },
  { id: "marketing", icon: "notifications.svg", label: "Marketing Updates", defaultChecked: false },
];

// dummy — static security question bank standing in for the real
// gxgetsecurityquestionsApi lookup until this demo is wired to data.
const DUMMY_SECURITY_QUESTIONS = [
  { value: 1, text: "What was the name of your first pet?" },
  { value: 2, text: "What city were you born in?" },
  { value: 3, text: "What is your mother's maiden name?" },
  { value: 4, text: "What was the make of your first car?" },
  { value: 5, text: "What was the name of your first school?" },
];

const SettingsStep: FC<SettingsStepProps> = ({ data, updateField, subStep, setSubStep }) => {
  // dummy — local only, this demo doesn't persist notification,
  // password, or security question fields anywhere.
  const [notificationState, setNotificationState] = useState<Record<string, boolean>>(
    () =>
      Object.fromEntries(
        DUMMY_NOTIFICATIONS.map((n) => [n.id, n.defaultChecked]),
      ),
  );
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [question1, setQuestion1] = useState<number | null>(null);
  const [answer1, setAnswer1] = useState("");
  const [question2, setQuestion2] = useState<number | null>(null);
  const [answer2, setAnswer2] = useState("");

  const toggleNotification = (id: string) => {
    setNotificationState((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const question1Options = DUMMY_SECURITY_QUESTIONS.filter(
    (item) => item.value !== question2,
  );
  const question2Options = DUMMY_SECURITY_QUESTIONS.filter(
    (item) => item.value !== question1,
  );

  return (
    <div className="space-y-6">
      <GxLineStepper steps={SETTINGS_SUBSTEPS} currentStep={subStep} showStepCounter />

      {subStep === 0 && (
        <>
          <div className="gx-profile-title">
            <h3 className="gx-h3-b">Set up your preferences</h3>
          </div>

          <div>
            <h4 className="gx-p12-b mb-2">Language & Time Zone</h4>
            <div className="notification-list-child notification-border-none">
              <div className="notification-img-child">
                <div className="notification-img">
                  <img src={`${ICON_BASE_PATH}lang.svg`} alt="Language" />
                </div>
                <p className="gx-p14-md">Language</p>
              </div>
              <div className="notification-switch">
                <GxSmallDropdown
                  value={data.language}
                  onChange={(val: string) => updateField("language", val)}
                  options={["English", "Arabic", "Spanish", "Catalan", "Portuguese"]}
                />
              </div>
            </div>
            <div className="notification-list-child no-border">
              <div className="notification-img-child">
                <div className="notification-img">
                  <img src={`${ICON_BASE_PATH}time_zone.svg`} alt="Time zone" />
                </div>
                <p className="gx-p14-md">Time Zone</p>
              </div>
              <div className="notification-switch">
                <GxSmallDropdown
                  value={data.timeZone}
                  onChange={(val: string) => updateField("timeZone", val)}
                  options={[
                    "Set Automatically",
                    "Asia/Dubai (UTC+4)",
                    "Europe/London (UTC+0)",
                    "America/New_York (UTC-5)",
                  ]}
                  searchable
                />
              </div>
            </div>
          </div>

          <div className="gx-profile-tab-heading flex items-center justify-between">
            <h3 className="gx-p14-md">Receive important updates on Whatsapp</h3>
            <Switch
              checked={data.whatsappOptIn}
              onChange={(e: any) => updateField("whatsappOptIn", e.value)}
            />
          </div>

          {data.whatsappOptIn && (
            <Mobilefiled
              label="Whatsapp Number"
              value={data.whatsappNumber}
              onChange={(val: string) => updateField("whatsappNumber", val)}
              countryDialCode={data.whatsappDialCode}
              onCountryCodeChange={(dialCode: string) => updateField("whatsappDialCode", dialCode)}
              triggerValidation={false}
              starlable
              disable={false}
            />
          )}
        </>
      )}

      {subStep === 1 && (
        <>
          <button className="gx-p12-sb text-gray-500 mb-2" onClick={() => setSubStep(0)}>
            ← Back to Preferences
          </button>

          <div className="gx-profile-title">
            <h3 className="gx-h3-b">Notifications</h3>
          </div>

          {DUMMY_NOTIFICATIONS.map((notification, index) => (
            <div
              className={`notification-list-child ${index === DUMMY_NOTIFICATIONS.length - 1 ? "no-border" : ""}`}
              key={notification.id}
            >
              <div className="notification-img-child">
                <div className="notification-img">
                  <img src={`${ICON_BASE_PATH}${notification.icon}`} alt={notification.label} />
                </div>
                <p className="gx-p14-md">{notification.label}</p>
              </div>
              <div className="notification-switch">
                <Switch
                  checked={notificationState[notification.id]}
                  onChange={() => toggleNotification(notification.id)}
                />
              </div>
            </div>
          ))}
        </>
      )}

      {subStep === 2 && (
        <>
          <button className="gx-p12-sb text-gray-500 mb-2" onClick={() => setSubStep(1)}>
            ← Back to Notifications
          </button>

          <div className="gx-profile-title">
            <h3 className="gx-h3-b">Change Password</h3>
          </div>

          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12">
              <OldPasswordFiled
                label="Old Password"
                value={oldPassword}
                onChange={setOldPassword}
                triggerValidation={false}
              />
            </div>
            <div className="col-span-12">
              <PasswordFiled
                label="New Password"
                value={newPassword}
                onChange={setNewPassword}
                triggerValidation={false}
              />
            </div>
            <div className="col-span-12">
              <ConfirmPasswordfiled
                label="Confirm Password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                matchValue={newPassword}
                triggerValidation={false}
              />
            </div>
            <div className="col-span-12">
              <div className="gx-forgot-password">
                <button type="button">Forgot Password</button>
              </div>
            </div>
          </div>
        </>
      )}

      {subStep === 3 && (
        <>
          <button className="gx-p12-sb text-gray-500 mb-2" onClick={() => setSubStep(2)}>
            ← Back to Change Password
          </button>

          <div className="gx-profile-title">
            <h3 className="gx-h3-b">Security Questions</h3>
          </div>

          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12">
              <p className="mb-3">Security Question 1</p>
              <SecuritySelectDropdown
                placeholder="Select a question"
                value={question1}
                onChange={(val: any) => setQuestion1(Number(val))}
                data={question1Options}
              />
            </div>

            <div className="col-span-12">
              <TextAreaInput
                value={answer1}
                rows={3}
                label="Answer"
                onChange={(e: any) => setAnswer1(e.target.value)}
              />
            </div>

            <div className="col-span-12">
              <p className="mb-3">Security Question 2</p>
              <SecuritySelectDropdown
                placeholder="Select a question"
                value={question2}
                onChange={(val: any) => setQuestion2(Number(val))}
                data={question2Options}
              />
            </div>

            <div className="col-span-12">
              <TextAreaInput
                value={answer2}
                rows={3}
                label="Answer"
                onChange={(e: any) => setAnswer2(e.target.value)}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SettingsStep;